import React, { FC, useMemo } from 'react';
import { Box } from '@mui/material';
import { SQUAD_TYPOGRAPHY } from './pageHeaderTokens';
import {
  formatSquadPiePercent,
  generateGradientColors,
  SQUAD_PORTFOLIO_PIE_FONT_FAMILY,
  SQUAD_PORTFOLIO_TEXT_COLOR,
  SQUAD_PIE_LEGEND_TOP_OFFSET_PX,
  squadPieLabelLiftPx,
  squadPieValueColumnPt,
  type SquadPieLegendAlign,
} from './squadPortfolioChartUtils';
import {
  squadDonutSegments,
  squadDonutSeparators,
  SQUAD_SEGOE_UI_METRICS,
  SQUAD_VECTOR_PIE,
  SQUAD_VECTOR_PIE_PX_PER_PT,
} from './squadVectorDonut';
import {
  SQUAD_DONUT_LABEL_COLOR_DARK,
  SQUAD_DONUT_LABEL_TOP_EM_PREVIEW,
  squadDonutLabelLayout,
  squadDonutTextWidth,
  squadPieVariant,
} from './squadDonutLabels';

type ChartItem = { label: string; value: number };

type Props = {
  data: ChartItem[];
  primaryColor: string;
  /** Excel Farbtheme colors (widget-mode parity). Falls back to primary gradient. */
  colors?: string[];
  /** `pieChartStyle` des Dokuments — mit oder ohne Zahlen am Ring (squadDonutLabels). */
  pieStyle?: string;
  /** Gemeinsame Höhe über dem Ring für alle Torten eines Bereichs. */
  reserveTopPt?: number;
  /** Legende auf Ringmitte oder oben bündig mit der Nachbarspalte — squadPieLegendAlign. */
  legendAlign?: SquadPieLegendAlign;
};

const px = (pt: number) => pt * SQUAD_VECTOR_PIE_PX_PER_PT;

/**
 * Preview mirror of VectorPieChartBlock (squadPdfParts): the same ring geometry in pt,
 * scaled to preview px, and a legend with the value in its own right-aligned column.
 * Plain Box text rather than Typography — body1's responsive font size would override it.
 */
export const SquadVectorPieChart: FC<Props> = ({
  data,
  primaryColor,
  colors: colorsProp,
  pieStyle,
  reserveTopPt,
  legendAlign = 'ring',
}) => {
  const colors = useMemo(() => {
    if (Array.isArray(colorsProp) && colorsProp.length > 0) return colorsProp;
    return generateGradientColors(primaryColor, Math.max(data.length, 1));
  }, [colorsProp, primaryColor, data.length]);

  const values = useMemo(() => data.map((item) => Number(item.value)), [data]);
  const valueColumnPt = squadPieValueColumnPt(values);
  const variant = squadPieVariant(pieStyle);
  const layout = useMemo(
    () => squadDonutLabelLayout(values, colors, variant.mode, { ...variant.options, reserveTopPt }),
    [values, colors, variant, reserveTopPt],
  );
  const segments = useMemo(
    () => squadDonutSegments(values, colors, layout.geom, layout.startAngle),
    [values, colors, layout],
  );
  const separators = useMemo(
    () => squadDonutSeparators(values, layout.geom, undefined, layout.startAngle),
    [values, layout],
  );

  return (
    <Box
      className="squad-vector-pie"
      sx={{
        display: 'flex',
        alignItems: legendAlign === 'top' ? 'flex-start' : 'center',
        gap: `${px(SQUAD_VECTOR_PIE.gapPt)}px`,
        width: '100%',
        minWidth: 0,
        fontFamily: SQUAD_PORTFOLIO_PIE_FONT_FAMILY,
        color: SQUAD_PORTFOLIO_TEXT_COLOR,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: px(layout.width),
          height: px(layout.height),
          flexShrink: 0,
          mt: `${-squadPieLabelLiftPx(legendAlign, px(layout.topPt))}px`,
        }}
      >
        <Box
          component="svg"
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          sx={{ width: px(layout.width), height: px(layout.height), display: 'block' }}
          aria-hidden
        >
          {segments.map((s, k) => (
            <path key={k} d={s.d} fill={s.color} />
          ))}
          {separators.map((d, k) => (
            <path key={`sep-${k}`} d={d} fill="none" stroke="#FFFFFF" strokeWidth={SQUAD_VECTOR_PIE.separatorPt} />
          ))}
          {layout.leaders.map((d, k) => (
            <path key={`lead-${k}`} d={d} fill="none" stroke={SQUAD_DONUT_LABEL_COLOR_DARK} strokeWidth={0.5} />
          ))}
        </Box>
        {layout.labels.map((label, k) => {
          const w = squadDonutTextWidth(label.text, label.fontPt, label.bold) + 4;
          return (
            <Box
              key={`lbl-${k}`}
              sx={{
                position: 'absolute',
                left: px(label.x - w / 2),
                top: px(label.y - label.fontPt * SQUAD_DONUT_LABEL_TOP_EM_PREVIEW),
                width: px(w),
                textAlign: 'center',
                fontSize: px(label.fontPt),
                lineHeight: 1,
                color: label.color,
                fontWeight: label.bold ? 700 : 400,
                whiteSpace: 'nowrap',
              }}
            >
              {label.text}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          // 'ring': um die Zeile über dem Ring versetzt → Legende auf Ringmitte (wie im PDF)
          mt: `${legendAlign === 'top' ? SQUAD_PIE_LEGEND_TOP_OFFSET_PX : px(layout.topPt)}px`,
          display: 'flex',
          flexDirection: 'column',
          gap: `${px(2)}px`,
          fontSize: SQUAD_TYPOGRAPHY.bar,
          lineHeight: 1.15,
        }}
      >
        {data.map((item, index) => (
          // Baseline: the empty mark ends on the text's first baseline; one cap height tall,
          // it spans exactly the figures (as markPt/markTopPt in the PDF).
          <Box key={index} sx={{ display: 'flex', alignItems: 'baseline', gap: `${px(3)}px`, minWidth: 0 }}>
            <Box
              sx={{
                width: `${SQUAD_SEGOE_UI_METRICS.capHeight}em`,
                height: `${SQUAD_SEGOE_UI_METRICS.capHeight}em`,
                flexShrink: 0,
                backgroundColor: colors[index % colors.length],
              }}
            />
            {/* „■ 52% Aktien“ — Wert vor dem Text (SQUAD-Wahl E1, 15.10.2026).
                +1 pt: preview type is set a little larger than the PDF's (11 px vs 8 pt) */}
            {variant.legendValues ? (
              <Box sx={{ width: px(valueColumnPt + 1), flexShrink: 0, textAlign: 'right', fontWeight: 700 }}>
                {formatSquadPiePercent(Number(item.value))}
              </Box>
            ) : null}
            <Box sx={{ flex: 1, minWidth: 0, whiteSpace: 'pre-line', overflowWrap: 'break-word', hyphens: 'auto' }}>
              {item.label}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
